/**
 * Funil Kanban (H09).
 *
 * ── Arrastar e soltar, com alternativa por teclado ────────────────────────
 *
 * A Técnica da task avisa: *"arrastar e soltar precisa de alternativa por
 * teclado — é um dos pontos mais fracos de Kanban em acessibilidade"*. Aqui cada
 * card tem um seletor de etapa que faz o mesmo movimento, e ele não é um plano B
 * escondido: também é o caminho mais rápido no toque, onde arrastar entre
 * colunas que não cabem na tela é sofrimento.
 *
 * Arrastar usa o DnD nativo do HTML, sem biblioteca. Não é minimalismo: as
 * bibliotecas de Kanban trazem o próprio modelo de foco e de anúncio, e
 * substituir o teclado do navegador é justamente o que quebra acessibilidade
 * nessas telas.
 *
 * ── O 409 é o assunto da task ─────────────────────────────────────────────
 *
 * Ver `mover.ts`. Aqui só se executa: o card volta, o aviso aparece com o
 * motivo, e o funil é recarregado quando o estado da tela deixou de valer.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ErroDaApi,
  JANELA_DO_ATUALIZAR_EM_SEGUNDOS,
  type CardDoFunil,
  type MudancaDeCampos,
} from '@cliqcrm/contracts';
import { camposDaRecusa, FiltroDeCampos } from '@/entities/campos/campo-personalizado';
import { PreencherParaMover, type MovimentoPendente } from './preencher-para-mover';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  useApi,
  useAtendimentosDoFunil,
  useBoardDoFunil,
  useCamposPersonalizados,
  useFunis,
} from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { useInvalidar } from '@/shared/api/provedor';
import { useEsperaDaProjecao } from '@/shared/api/espera-da-projecao';
import { useObservarRecurso } from '@/shared/api/assinaturas';
import { useSessao } from '@/shared/sessao/contexto';
import { configuraOAtualizar, LE_OPORTUNIDADE } from '@/entities/acesso/papel';
import { Carregando, Defasado, Erro, Vazio } from '@/shared/ui/estados/estados';
import { DialogoDeNovaOportunidade, type DadosDaNovaOportunidade } from './nova-oportunidade';
import { DialogoDeEdicaoDaOportunidade, type EdicaoDaOportunidade } from './editar-oportunidade';
import { DialogoDaOportunidade } from '@/widgets/detalhe-da-oportunidade';
import { DialogoDePerda, type PerdaEscolhida } from './dialogo-de-perda';
import {
  comMovimento,
  comMovimentoConfirmado,
  comVersaoFresca,
  conciliar,
  esperandoOBoard,
  diagnosticarCriacao,
  diagnosticarMovimento,
  versaoParaEnviar,
  semMovimento,
  type DiagnosticoDoMovimento,
  type MovimentosEmCurso,
  type VersoesFrescas,
} from '../lib/mover';
import './funil.css';
import { Botao, Campo, CampoSelecao, Dialogo, FiltroPilula, Icone } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { nomeDaEtapa } from '@/entities/comercial/etapa';
import { NovaTarefa } from '@/features/comercial/criar-tarefa';
import { Colunas } from './colunas';
import { RotinasDoAtualizar } from './rotinas-do-atualizar';

export function Funil({
  aoAbrirConversa,
  aoAbrirCliente,
  aoConfigurarEtapas,
}: {
  aoAbrirConversa: (conversaId: string) => void;
  /** Do card da oportunidade para o Cliente 360 (28/09/2026). */
  aoAbrirCliente?: (clienteId: string) => void;
  /**
   * As etapas do funil (28/10): um botão no topo do quadro, onde se mexe nele.
   * A tela é a das Configurações, e quem a abre é a casca.
   */
  aoConfigurarEtapas?: () => void;
}): ReactNode {
  const api = useApi();
  const invalidar = useInvalidar();

  /*
   * O quadro ao vivo: a oportunidade movida por outra pessoa ou por uma
   * automação aparece na coluna nova sem recarregar (26/10: a URA moveu o card,
   * e o quadro aberto continuou mostrando a etapa de antes). Só quem lê
   * oportunidade assina: a assinatura recusada derrubaria o fluxo inteiro.
   */
  const { estado } = useSessao();
  const papel = estado.nome === 'autenticado' ? estado.me.papel : null;
  useObservarRecurso(papel !== null && LE_OPORTUNIDADE.has(papel) ? 'funil:eu' : null);

  /*
   * QUAL funil este quadro mostra.
   *
   * `null` é "ainda não escolhi" — a lista chega depois da primeira pintura,
   * e um estado iniciado com a primeira chave nasceria vazio e ficaria assim.
   *
   * A reserva é a PRIMEIRA da lista, e não `padrao` fixo: a revenda que moveu
   * tudo para funis nomeados não tem funil `padrao`, e pedi-lo responde 404 —
   * o quadro abriria em erro para quem fez exatamente o que a tela ofereceu.
   */
  const funis = useFunis();
  const [funilEscolhido, definirFunilEscolhido] = useState<string | null>(null);
  const listaDeFunis = funis.data?.itens ?? [];
  const escolhido = funilEscolhido ?? listaDeFunis[0]?.chave ?? null;

  const [soMeus, definirSoMeus] = useState(false);
  const [soEmRisco, definirSoEmRisco] = useState(false);
  const [busca, definirBusca] = useState('');
  /*
   * A busca que vai ao servidor, depois que a pessoa para de digitar: o quadro
   * inteiro a cada letra gastaria o limite de requisições à toa.
   */
  const [buscaAplicada, definirBuscaAplicada] = useState('');
  useEffect(() => {
    const espera = setTimeout(() => definirBuscaAplicada(busca.trim()), 300);
    return () => clearTimeout(espera);
  }, [busca]);
  /** O filtro por campo personalizado (#568), no texto que a API lê. */
  const [filtroDeCampos, definirFiltroDeCampos] = useState('');
  const camposDaOportunidade = useCamposPersonalizados('oportunidade');

  const filtro = useMemo(
    () => ({
      ...(soMeus ? { responsavel: 'eu' } : {}),
      ...(soEmRisco ? { em_risco: 'true' } : {}),
      ...(buscaAplicada ? { busca: buscaAplicada } : {}),
      ...(filtroDeCampos ? { campos: filtroDeCampos } : {}),
    }),
    [soMeus, soEmRisco, buscaAplicada, filtroDeCampos],
  );

  /* Sem funil escolhido a consulta não sai — ver `useBoardDoFunil`. */
  const consulta = useBoardDoFunil(escolhido, filtro);
  const [emCurso, definirEmCurso] = useState<MovimentosEmCurso>(new Map());
  /*
   * As versões devolvidas pelo servidor, até a projeção alcançar.
   *
   * Ver o cabeçalho de `versaoParaEnviar` em `mover.ts`: o board lê a versão da
   * projeção, que depende do webhook do Twenty. Sem isto, o segundo arrasto do
   * mesmo card é recusado com 409 acusando uma concorrência que não houve.
   */
  const [versoesFrescas, definirVersoesFrescas] = useState<VersoesFrescas>(new Map());
  const [recusa, definirRecusa] = useState<DiagnosticoDoMovimento | null>(null);
  const [arrastando, definirArrastando] = useState<string | null>(null);
  const [recado, definirRecado] = useState<string | null>(null);
  /*
   * A oportunidade recém-criada, até o board mostrá-la.
   *
   * Casa por `referencia_externa`, e não por `id`: o canônico é cunhado na
   * ingestão (C13) e vem NULO na resposta da criação, enquanto a referência da
   * engine existe desde o primeiro instante. Esperar pelo `id` seria esperar
   * por algo que a resposta não tem.
   */
  const [recemCriada, definirRecemCriada] = useState<string | null>(null);

  /*
   * A oportunidade em edição, e a versão que o board tinha quando salvamos.
   *
   * Duas coisas, e não uma: o card que está no formulário, e — depois de
   * salvar — a referência a esperar. Editar sofre a MESMA defasagem que criar
   * (o board lê projeção, alimentada por webhook), mas aqui "apareceu" não
   * serve como sinal, porque o card já estava lá. O sinal é a VERSÃO mudar.
   */
  const [editando, definirEditando] = useState<CardDoFunil | null>(null);
  /*
   * Excluir pelo card (30/09/2026). O card some da tela assim que o CRM
   * confirma, sem esperar a projeção: é ela que tira o card do quadro, pelo
   * aviso do CRM, e até lá ele voltaria a cada recarga — parecendo que a
   * exclusão não pegou.
   */
  const [excluindo, definirExcluindo] = useState<CardDoFunil | null>(null);
  const [excluindoAgora, definirExcluindoAgora] = useState(false);
  const [erroDaExclusao, definirErroDaExclusao] = useState<string | null>(null);
  const [excluidas, definirExcluidas] = useState<ReadonlySet<string>>(new Set());
  const [salvandoEdicao, definirSalvandoEdicao] = useState(false);
  const [editada, definirEditada] = useState<{ referencia: string; versao: string } | null>(null);
  const [erroDaEdicao, definirErroDaEdicao] = useState<string | null>(null);
  const [anotando, definirAnotando] = useState(false);
  const [erroDaNota, definirErroDaNota] = useState<string | null>(null);
  const [corrigindo, definirCorrigindo] = useState(false);
  const [erroDaCorrecao, definirErroDaCorrecao] = useState<string | null>(null);
  /*
   * A ficha da oportunidade, aberta pelo clique no card.
   *
   * Estado próprio, e não `editando` reaproveitado: os dois diálogos coexistem
   * no fluxo — abre-se a ficha, clica-se em "Editar dados", e ao fechar a
   * edição a ficha continua atrás. Um estado só faria o fechamento da edição
   * levar a ficha junto.
   */
  const [vendo, definirVendo] = useState<CardDoFunil | null>(null);

  /*
   * O "Atualizar" (28/10): relê o quadro na hora e adianta, no serviço, as
   * rotinas que criam e movem cards neste funil. O servidor trava 2 minutos por
   * funil para a organização toda; a tela mostra quanto falta, contado de
   * `proxima_em` (ou do `Retry-After`, quando alguém clicou antes). O botão só
   * existe no funil em que o Super Admin marcou rotinas (#1136), e é ele quem
   * as escolhe, pelo "Rotinas do Atualizar".
   */
  const funilAberto = listaDeFunis.find((f) => f.chave === escolhido);
  const comAtualizar = funilAberto?.com_atualizar === true;
  const [escolhendoRotinas, definirEscolhendoRotinas] = useState(false);
  const [travaDoAtualizar, definirTravaDoAtualizar] = useState<{
    funil: string;
    ate: number;
  } | null>(null);
  const [agora, definirAgora] = useState(() => Date.now());
  const restanteDoAtualizar =
    travaDoAtualizar && travaDoAtualizar.funil === escolhido
      ? Math.max(0, Math.ceil((travaDoAtualizar.ate - agora) / 1000))
      : 0;
  useEffect(() => {
    if (!travaDoAtualizar) return;
    const relogio = setInterval(() => {
      const instante = Date.now();
      definirAgora(instante);
      if (instante >= travaDoAtualizar.ate) definirTravaDoAtualizar(null);
    }, 1_000);
    return () => clearInterval(relogio);
  }, [travaDoAtualizar]);
  const atualizacao = useMutation({
    mutationFn: (funil: string) => api!.atualizarFunil({ caminho: { id: funil } }),
    onMutate: () => invalidar([chaves.funil()]),
    onSuccess: (r, funil) => {
      definirAgora(Date.now());
      definirTravaDoAtualizar({ funil, ate: Date.parse(r.proxima_em) });
      // Os cards chegam pelo fluxo ao vivo, quando a rotina terminar.
      definirRecado(
        r.disparadas === 0
          ? 'Quadro atualizado.'
          : r.disparadas === 1
            ? '1 rotina rodando: os cards chegam em instantes.'
            : `${r.disparadas} rotinas rodando: os cards chegam em instantes.`,
      );
    },
    onError: (erro: unknown, funil) => {
      if (erro instanceof ErroDaApi && erro.codigo === 'LIMITE_EXCEDIDO') {
        definirAgora(Date.now());
        definirTravaDoAtualizar({
          funil,
          ate: Date.now() + (erro.esperarSegundos ?? JANELA_DO_ATUALIZAR_EM_SEGUNDOS) * 1000,
        });
        // O 429 não diz quem clicou: pode ter sido a própria pessoa, noutra aba.
        definirRecado(
          'Quadro relido. O funil acabou de ser atualizado; as rotinas já estão rodando.',
        );
        return;
      }
      definirRecado(recadoDeErro(erro));
    },
  });
  const minutosESegundos = `${Math.floor(restanteDoAtualizar / 60)}:${String(
    restanteDoAtualizar % 60,
  ).padStart(2, '0')}`;

  const board = consulta.data;
  /** O que cada card é no SISAC (#1151): só na clínica, e depois do quadro. */
  const clinica = estado.nome === 'autenticado' && (estado.me.flags['clinica'] ?? false);
  const atendimentos = useAtendimentosDoFunil(
    escolhido,
    filtro,
    consulta.isPlaceholderData ? undefined : board,
    clinica,
  );
  const atendimentoDoCard = useMemo(
    () => new Map((atendimentos.data?.cards ?? []).map((item) => [item.card, item.atendimento])),
    [atendimentos.data],
  );

  /*
   * Quem larga o movimento é o BOARD, quando ele mesmo já mostra o card no
   * destino. É o outro lado de `comMovimentoConfirmado`: sem isto o card ficaria
   * no destino para sempre, inclusive depois de outra pessoa movê-lo de volta.
   */
  useEffect(() => {
    if (!board) return;
    const cards = board.colunas.flatMap((c) => c.cards);
    definirEmCurso((atuais) => conciliar(atuais, cards));
  }, [board]);

  /*
   * E se a projeção ainda não tiver chegado, pergunta de novo.
   *
   * A projeção mede 33 ms, então quase sempre a primeira rebusca já concorda e
   * isto nem chega a rodar. O caso que ele cobre é a fila presa — e mesmo aí
   * para sozinho depois do teto, porque o card continua no lugar certo de
   * qualquer forma.
   */
  useEffect(() => {
    if (!esperandoOBoard(emCurso)) return;
    const t = setTimeout(() => invalidar([chaves.funil()]), 1_200);
    return () => clearTimeout(t);
  }, [emCurso, board, invalidar]);

  /*
   * O mesmo para a oportunidade recém-criada: quem larga a espera é o board,
   * quando ela aparece nele. A diferença é que criar não tem estado otimista —
   * ver o cabeçalho de `espera-da-projecao.ts` para por que aqui isso não cabe.
   */
  useEffect(() => {
    if (!recemCriada || !board) return;
    const apareceu = board.colunas.some((coluna) =>
      coluna.cards.some((card) => card.referencia_externa === recemCriada),
    );
    if (apareceu) definirRecemCriada(null);
  }, [board, recemCriada]);

  useEsperaDaProjecao([chaves.funil()], recemCriada !== null);

  useEffect(() => {
    if (!editada || !board) return;
    const noBoard = board.colunas
      .flatMap((coluna) => coluna.cards)
      .find((card) => card.referencia_externa === editada.referencia);
    // Versão diferente da que salvamos = a projeção alcançou.
    if (noBoard && noBoard.version !== editada.versao) definirEditada(null);
  }, [board, editada]);

  useEsperaDaProjecao([chaves.funil()], editada !== null);

  /*
   * O movimento que a etapa de destino recusou por campo vazio (#568): a tela
   * pede os campos, salva e move de novo. Ver `preencher-para-mover.tsx`.
   */
  const [pendente, definirPendente] = useState<MovimentoPendente | null>(null);
  const [salvandoPendente, definirSalvandoPendente] = useState(false);
  const [erroDoPendente, definirErroDoPendente] = useState<string | null>(null);

  const mover = useCallback(
    (card: CardDoFunil, etapaDestino: string, perda?: PerdaEscolhida, versao?: string) => {
      if (!api || !card.id) return;

      // Otimista: o card aparece no destino agora. Ver o cabeçalho de `mover.ts`
      // para por que aqui isso é aceitável e no envio de mensagem não é.
      definirEmCurso((atuais) => comMovimento(atuais, card.id!, etapaDestino));
      definirRecusa(null);

      void api
        .moverEtapa({
          caminho: { id: card.id },
          // A versão que veio na leitura, devolvida sem interpretar. Sem ela,
          // dois vendedores movendo o mesmo card produzem última-escrita-vence.
          corpo: {
            etapa: etapaDestino,
            // A versão dita vence a fresca: é a do salvamento dos campos que
            // acabou de acontecer, mais nova que qualquer outra que a tela tenha.
            version: versao ?? versaoParaEnviar(card, versoesFrescas),
            ...(perda ? { perda } : {}),
          },
        })
        .then((oportunidade) => {
          // A versão nova, guardada ANTES de invalidar: a rebusca pode trazer a
          // projeção ainda antiga, e é justamente esse intervalo que isto cobre.
          if (card.id) {
            definirVersoesFrescas((atuais) =>
              comVersaoFresca(atuais, card.id!, oportunidade.version),
            );
            // O card FICA no destino até o board concordar. Ver o cabeçalho de
            // `comMovimentoConfirmado`: apagar aqui é o que fazia o card voltar
            // depois de o servidor ter aceitado.
            definirEmCurso((atuais) => comMovimentoConfirmado(atuais, card.id!));
          }
          invalidar([chaves.funil(), chaves.timeline()]);
        })
        .catch((erro: unknown) => {
          const faltam = erro instanceof ErroDaApi ? camposDaRecusa(erro.problema.errors) : [];
          if (faltam.length > 0) {
            definirEmCurso((atuais) => semMovimento(atuais, card.id!));
            const coluna = board?.colunas.find((item) => item.etapa === etapaDestino);
            definirErroDoPendente(null);
            definirPendente({
              card,
              etapa: etapaDestino,
              nomeDaEtapa: coluna ? nomeDaEtapa(coluna.rotulo) : etapaDestino,
              campos: faltam,
              perda,
            });
            return;
          }
          const diagnostico = diagnosticarMovimento(erro);
          definirRecusa(diagnostico);
          // O card volta ANTES de qualquer rebusca: deixá-lo no destino
          // enquanto a atualização chega é a tela mentindo por um instante.
          definirEmCurso((atuais) => semMovimento(atuais, card.id!));
          if (diagnostico.recarregar) invalidar([chaves.funil()]);
        });
    },
    [api, board, invalidar, versoesFrescas],
  );

  const preencherEMover = useCallback(
    (mudanca: MudancaDeCampos) => {
      const espera = pendente;
      if (!api || !espera?.card.id) return;
      definirSalvandoPendente(true);
      definirErroDoPendente(null);
      void api
        .editarOportunidade({
          caminho: { id: espera.card.id },
          corpo: { campos: mudanca, version: versaoParaEnviar(espera.card, versoesFrescas) },
        })
        .then((salva) => {
          definirPendente(null);
          mover(
            { ...espera.card, campos: salva.campos },
            espera.etapa,
            espera.perda as PerdaEscolhida | undefined,
            salva.version,
          );
        })
        .catch((erro: unknown) =>
          definirErroDoPendente(
            recadoDeErro(erro, { generica: 'Não foi possível salvar os campos. Tente de novo.' }),
          ),
        )
        .finally(() => definirSalvandoPendente(false));
    },
    [api, mover, pendente, versoesFrescas],
  );

  /**
   * Criar tarefa a partir da oportunidade (H10).
   *
   * A Técnica da H10: *"criar a partir do contexto, com vínculo preenchido.
   * Criar tarefa solta e depois vincular é o caminho que ninguém completa."*
   * Daqui o alvo sai pronto — a oportunidade que está na mão.
   */
  /*
   * Criar oportunidade DAQUI (H09).
   *
   * O funil é onde o vendedor está quando percebe que existe um negócio.
   * Mandá-lo abrir o CRM para registrar é o caminho que ninguém percorre — e o
   * negócio fica sem registro, que é o que o funil existe para evitar.
   */
  const [criando, definirCriando] = useState(false);
  const [salvando, definirSalvando] = useState(false);

  const excluirOportunidade = useCallback(() => {
    const card = excluindo;
    if (!api || !card?.id) return;
    const id = card.id;

    definirExcluindoAgora(true);
    definirErroDaExclusao(null);
    void api
      .removerOportunidade({ caminho: { id } })
      .then(() => {
        definirExcluidas((atuais) => new Set(atuais).add(id));
        invalidar([chaves.funil(), chaves.timeline()]);
        // Só fecha a pergunta se ela ainda é sobre ESTE card.
        definirExcluindo((atual) => (atual?.id === id ? null : atual));
        definirRecado('Oportunidade excluída com sucesso.');
      })
      .catch((erro: unknown) =>
        definirErroDaExclusao(
          recadoDeErro(erro, { generica: 'Não foi possível excluir agora. Tente de novo.' }),
        ),
      )
      .finally(() => definirExcluindoAgora(false));
  }, [api, excluindo, invalidar]);

  const editarOportunidade = useCallback(
    (mudancas: EdicaoDaOportunidade) => {
      const card = editando;
      if (!api || !card?.id) return;

      definirSalvandoEdicao(true);
      definirErroDaEdicao(null);
      void api
        .editarOportunidade({
          caminho: { id: card.id },
          // A versão que veio no card: é ela que recusa a escrita quando
          // alguém mexeu entre o carregamento e o salvamento.
          corpo: { ...mudancas, version: card.version },
        })
        .then(() => {
          // A versão ANTIGA é o que a espera compara: quando o board trouxer
          // outra, a projeção alcançou. Ver o efeito lá acima.
          definirEditada({ referencia: card.referencia_externa, versao: card.version });
          invalidar([chaves.funil(), chaves.timeline()]);
          definirEditando(null);
          definirRecado('Oportunidade editada.');
        })
        .catch((erro: unknown) =>
          definirErroDaEdicao(
            recadoDeErro(erro, { generica: 'Não foi possível salvar a edição. Tente de novo.' }),
          ),
        )
        .finally(() => definirSalvandoEdicao(false));
    },
    [api, editando, invalidar],
  );

  /*
   * Anotar é escrita PRÓPRIA, e não parte do salvamento.
   *
   * A nota não depende de `version`: ela acrescenta uma linha, e duas pessoas
   * anotando ao mesmo tempo produzem duas notas — que é o resultado certo.
   * Amarrá-la ao Salvar faria o conflito de versão de OUTRO campo levar junto o
   * que a pessoa acabou de escrever.
   */
  const anotar = useCallback(
    (texto: string) => {
      const card = vendo;
      if (!api || !card?.id) return;

      definirAnotando(true);
      definirErroDaNota(null);
      void api
        .criarNotaDaOportunidade({
          caminho: { id: card.id },
          corpo: { texto },
          /*
           * Chave por TENTATIVA, como na criação da oportunidade — e aqui ela
           * importa mais: a nota não pode ser apagada por rota nenhuma, então a
           * duplicata do segundo clique ficaria na tabela para sempre.
           */
          idempotencyKey:
            globalThis.crypto?.randomUUID?.() ?? `nota-${Date.now()}-${Math.random()}`,
        })
        .then(() => {
          // Só as notas DESTA oportunidade: o board não muda por causa de uma
          // nota, e invalidá-lo recarregaria o quadro inteiro à toa.
          invalidar([chaves.notasDaOportunidade(card.id!)]);
        })
        .catch((erro: unknown) =>
          definirErroDaNota(
            recadoDeErro(erro, { generica: 'Não foi possível anotar. Tente de novo.' }),
          ),
        )
        .finally(() => definirAnotando(false));
    },
    [api, vendo, invalidar],
  );

  /*
   * Corrigir o texto de uma nota — só do diálogo de editar.
   *
   * Não passa por `version`: a nota tem um campo só, e duas pessoas corrigindo
   * a mesma linha não têm partes diferentes para conciliar. O que havia antes
   * fica na trilha, que é onde se recupera.
   */
  const corrigirNota = useCallback(
    (notaId: string, texto: string) => {
      const card = editando;
      if (!api || !card?.id) return;

      definirCorrigindo(true);
      definirErroDaCorrecao(null);
      void api
        .editarNotaDaOportunidade({
          caminho: { id: card.id, notaId },
          corpo: { texto },
        })
        .then(() => invalidar([chaves.notasDaOportunidade(card.id!)]))
        .catch((erro: unknown) =>
          definirErroDaCorrecao(
            recadoDeErro(erro, { generica: 'Não foi possível salvar a correção. Tente de novo.' }),
          ),
        )
        .finally(() => definirCorrigindo(false));
    },
    [api, editando, invalidar],
  );

  const criarOportunidade = useCallback(
    (dados: DadosDaNovaOportunidade) => {
      if (!api) return;

      definirSalvando(true);
      definirRecusa(null);
      void api
        .criarOportunidade({
          corpo: {
            nome: dados.nome,
            ...(dados.etapa ? { etapa: dados.etapa } : {}),
            /*
             * O funil ABERTO, quando a etapa ficou em branco. Sem isto, "primeira
             * etapa do funil" criava no funil padrão, e a oportunidade sumia do
             * quadro em que a pessoa estava. Com etapa, ela já diz o funil.
             */
            ...(!dados.etapa && board ? { funil: board.funil.chave } : {}),
            ...(dados.valor ? { valor: dados.valor } : {}),
            // O canônico do cliente (§5.1), quando a pessoa escolheu um.
            ...(dados.clienteId ? { cliente_id: dados.clienteId } : {}),
            // O que foi combinado, escrito antes de o negócio existir. Vazio
            // não vai: o contrato lê ausência como "não há nota".
            ...(dados.notas && dados.notas.length > 0 ? { notas: [...dados.notas] } : {}),
            ...(dados.campos ? { campos: dados.campos } : {}),
          },
          /*
           * Chave por TENTATIVA, e não por conteúdo.
           *
           * Duas oportunidades com o mesmo nome são um caso legítimo — dois
           * clientes querendo o mesmo carro. Derivar a chave do nome faria a
           * segunda ser engolida como repetição da primeira, e o vendedor
           * ficaria sem o card sem nenhum erro na tela.
           */
          idempotencyKey:
            globalThis.crypto?.randomUUID?.() ?? `oportunidade-${Date.now()}-${Math.random()}`,
        })
        .then((criada) => {
          definirRecemCriada(criada.referencia_externa);
          invalidar([chaves.funil(), chaves.timeline()]);
          definirCriando(false);
          definirRecado('Oportunidade criada.');
        })
        .catch((erro: unknown) => definirRecusa(diagnosticarCriacao(erro)))
        .finally(() => definirSalvando(false));
    },
    [api, invalidar, board],
  );

  /*
   * As etapas dos OUTROS funis, para o "Mover para" do cartão.
   *
   * Levar a oportunidade para outro funil conserta o que caiu no lugar
   * errado. A lista vem da mesma rota da tela de Configurações, e cada etapa
   * já diz de que funil é: a tela não reescreve a convenção do prefixo.
   */
  const todasAsEtapas = useQuery({
    queryKey: chaves.etapasDoFunil(),
    enabled: api !== null,
    queryFn: ({ signal }) => api!.etapasDoFunil({ sinal: signal }),
  });
  const outrosFunis = useMemo(() => {
    const atual = board?.funil.chave;
    return listaDeFunis
      .filter((f) => f.chave !== atual)
      .map((f) => ({
        nome: f.nome,
        etapas: (todasAsEtapas.data?.itens ?? [])
          .filter((e) => e.funil === f.chave)
          .sort((a, b) => a.posicao - b.posicao)
          .map((e) => ({ etapa: e.valor, rotulo: nomeDaEtapa(e.rotulo) })),
      }))
      .filter((f) => f.etapas.length > 0);
  }, [board, listaDeFunis, todasAsEtapas.data]);

  /*
   * As etapas de PERDA, deste funil e dos outros (058).
   *
   * Levar um negócio para uma delas pergunta o motivo antes de mover. A lista
   * junta as colunas do quadro e as etapas dos outros funis, porque o "Mover
   * para" também leva para a perda de outro funil.
   */
  const etapasDePerda = useMemo(
    () =>
      new Set([
        ...(board?.colunas ?? []).filter((c) => c.resultado === 'perdido').map((c) => c.etapa),
        ...(todasAsEtapas.data?.itens ?? [])
          .filter((e) => e.resultado === 'perdido')
          .map((e) => e.valor),
      ]),
    [board, todasAsEtapas.data],
  );
  const [perdendo, definirPerdendo] = useState<{ card: CardDoFunil; etapa: string } | null>(null);
  const pedirMovimento = useCallback(
    (card: CardDoFunil, etapa: string) => {
      if (etapasDePerda.has(etapa)) definirPerdendo({ card, etapa });
      else mover(card, etapa);
    },
    [etapasDePerda, mover],
  );

  /*
   * As etapas para o diálogo, na ORDEM do quadro.
   *
   * `posicao` e não a ordem que o servidor devolveu: é a mesma regra que as
   * colunas usam para se desenhar, e duas ordens diferentes para a mesma coisa
   * fariam a lista do diálogo discordar do que está na tela atrás dele.
   */
  const etapasEmOrdem = useMemo(
    () =>
      [...(board?.colunas ?? [])]
        .sort((a, b) => a.posicao - b.posicao)
        .map((coluna) => ({ chave: coluna.etapa, nome: nomeDaEtapa(coluna.rotulo) })),
    [board],
  );

  /*
   * O card com o formulário de nova tarefa aberto.
   *
   * Antes o ícone criava num clique uma tarefa de título fixo ("Retomar …"),
   * sem prazo e sem responsável. Agora abre o mesmo formulário do atendimento
   * e do Cliente 360; o vínculo com a oportunidade continua vindo do card.
   */
  const [criandoTarefa, definirCriandoTarefa] = useState<CardDoFunil | null>(null);

  /*
   * A lista de funis falhou — e isto precisa de ramo PRÓPRIO.
   *
   * Sem funil escolhido o quadro nem consulta, então cair no `isPending` de
   * baixo deixaria o esqueleto girando para sempre: a tela ficaria carregando
   * algo que ninguém pediu, sem erro nenhum e sem botão de tentar de novo.
   */
  if (escolhido === null && funis.isError && funis.data === undefined) {
    return (
      <div className="funil funil--centro">
        <Erro
          erro={funis.error}
          aoRepetir={() => void funis.refetch()}
          titulo="Não foi possível carregar os funis"
        />
      </div>
    );
  }

  if (consulta.isPending) {
    return (
      <div className="funil">
        <Carregando forma="cartoes" linhas={6} rotulo="Carregando o funil" />
      </div>
    );
  }

  // Só sem nada na tela: com dados carregados, uma atualização que falhou
  // (o limite de requisições, a rede) não apaga o que estava certo.
  if (consulta.error && consulta.data === undefined) {
    return (
      <div className="funil funil--centro">
        <Erro
          erro={consulta.error}
          aoRepetir={() => void consulta.refetch()}
          titulo="Não foi possível carregar o funil"
        />
      </div>
    );
  }

  if (!board) return <div className="funil" />;

  /*
   * Quantos dos cards CARREGADOS estão em risco, para o selo da barra. O quadro
   * traz uma fatia por coluna; com coluna cortada o número é "pelo menos", e o
   * selo diz isso com o "+".
   */
  const emRiscoCarregados = board.colunas.reduce(
    (soma, coluna) => soma + coluna.cards.filter((c) => c.risco.em_risco).length,
    0,
  );
  const emRisco = board.colunas.some((c) => c.tem_mais)
    ? `${emRiscoCarregados}+`
    : String(emRiscoCarregados);

  return (
    <div className="funil">
      {/*
        O topo e o quadro num painel só (desenho de 26/10/2026): "Funil", os
        controles e "Nova oportunidade" numa barra, com a régua embaixo, e as
        colunas logo abaixo. Sem o subtítulo: qual funil é, o seletor diz; com
        um funil só, o nome fica no lugar dele.
      */}
      <div className="funil__painel">
        <header className="funil__barra">
          <h1 className="funil__titulo" title={`Funil ${board.funil.numero}`}>
            Funil
          </h1>
          {listaDeFunis.length <= 1 && (
            <span className="funil__nome" title={`Funil ${board.funil.numero}`}>
              {board.funil.nome}
            </span>
          )}
          <div className="funil__filtros">
            {/*
              O seletor vem ANTES dos filtros, e só aparece com mais de um
              funil.

              A ordem é a da decisão: o funil diz QUAL quadro é, e os filtros
              recortam o que já está nele. E um seletor de uma opção só é
              ruído — a revenda que nunca criou um segundo funil não tem nada a
              escolher.
            */}
            {listaDeFunis.length > 1 && (
              <CampoSelecao
                rotuloOculto="Funil"
                value={escolhido ?? ''}
                onChange={(e) => definirFunilEscolhido(e.currentTarget.value)}
              >
                {listaDeFunis.map((f) => (
                  <option key={f.chave} value={f.chave}>
                    {f.nome}
                  </option>
                ))}
              </CampoSelecao>
            )}
            <CampoSelecao
              rotuloOculto="Responsável"
              value={soMeus ? 'meus' : 'todos'}
              onChange={(e) => definirSoMeus(e.currentTarget.value === 'meus')}
            >
              <option value="todos">Responsável: todos</option>
              <option value="meus">Responsável: eu</option>
            </CampoSelecao>
            {/*
              O selo diz QUANTOS estão em risco antes do clique, e o clique
              filtra: o número é o convite (26/10/2026).
            */}
            <FiltroPilula ligado={soEmRisco} aoAlternar={() => definirSoEmRisco((v) => !v)}>
              <span className="funil__risco-ponto" aria-hidden="true" />
              Em risco · {emRisco}
            </FiltroPilula>
            <Campo
              type="search"
              rotuloOculto="Buscar oportunidade"
              value={busca}
              onChange={(e) => definirBusca(e.target.value)}
              placeholder="Buscar oportunidade"
            />
            {/*
              Depois dos filtros e visualmente diferente deles: filtro muda o
              que se vê, e este botão cria algo. Um terceiro "alternador" ao
              lado dos outros dois seria lido como mais um filtro.
            */}
            <span className="funil__criar">
              {comAtualizar && escolhido && (
                <Botao
                  variante="secundario"
                  desabilitado={atualizacao.isPending || restanteDoAtualizar > 0}
                  motivo={
                    restanteDoAtualizar > 0
                      ? `Disponível em ${minutosESegundos}: o funil se atualiza uma vez a cada ${
                          JANELA_DO_ATUALIZAR_EM_SEGUNDOS / 60
                        } minutos.`
                      : 'Atualizando…'
                  }
                  onClick={() => atualizacao.mutate(escolhido)}
                >
                  <Icone nome="repetir" />
                  {restanteDoAtualizar > 0 ? `Disponível em ${minutosESegundos}` : 'Atualizar'}
                </Botao>
              )}
              {configuraOAtualizar(papel) && escolhido && (
                <Botao
                  variante="secundario"
                  icone
                  rotulo="Rotinas do Atualizar"
                  onClick={() => definirEscolhendoRotinas(true)}
                >
                  <Icone nome="configuracoes" />
                </Botao>
              )}
              {aoConfigurarEtapas && (
                <Botao variante="secundario" onClick={aoConfigurarEtapas}>
                  Configurar etapas
                </Botao>
              )}
              <Botao variante="primario" onClick={() => definirCriando(true)}>
                Nova oportunidade
              </Botao>
            </span>
          </div>
        </header>

        {/* O filtro por campo personalizado (#568), embaixo do cabeçalho. */}
        <div className="funil__filtro-de-campos">
          <FiltroDeCampos
            id="funil-filtro"
            campos={camposDaOportunidade.data?.itens ?? []}
            filtro={filtroDeCampos}
            aoMudar={definirFiltroDeCampos}
          />
        </div>

        {/* O corpo rola; o cabeçalho fica. */}
        <div className="funil__corpo">
          {board.frescor.stale && (
            <Defasado
              projetadoEm={board.frescor.projetado_em}
              origem="Este funil"
              aoAtualizar={() => void consulta.refetch()}
            />
          )}

          {recusa && (
            <Faixa
              tom="bloqueado"
              acao={{ rotulo: 'Entendi', aoClicar: () => definirRecusa(null) }}
            >
              <strong>{recusa.motivo}</strong> {recusa.acao}
              {recusa.correlationId && (
                <>
                  {' '}
                  <code>{recusa.correlationId}</code>
                </>
              )}
            </Faixa>
          )}

          {board.colunas.length === 0 ? (
            <div className="funil__centro">
              <Vazio
                titulo="Nenhuma etapa configurada"
                porque="O funil da sua organização ainda não tem etapas. Quem administra as cria em “Configurar etapas”."
                {...(aoConfigurarEtapas
                  ? { acao: { rotulo: 'Configurar etapas', aoClicar: aoConfigurarEtapas } }
                  : {})}
              />
            </div>
          ) : (
            <Colunas
              board={board}
              outrosFunis={outrosFunis}
              emCurso={emCurso}
              arrastando={arrastando}
              aoArrastar={definirArrastando}
              aoMover={pedirMovimento}
              aoAbrirConversa={aoAbrirConversa}
              aoCriarTarefa={definirCriandoTarefa}
              aoEditar={definirEditando}
              aoExcluir={(card) => {
                definirErroDaExclusao(null);
                definirExcluindo(card);
              }}
              excluidas={excluidas}
              atendimentos={atendimentoDoCard}
              aoAbrir={definirVendo}
            />
          )}
        </div>
      </div>

      {criandoTarefa?.id && (
        <NovaTarefa
          vinculo={{
            alvo: { tipo: 'oportunidade', id: criandoTarefa.id },
            rotulo: criandoTarefa.titulo ?? 'esta oportunidade',
          }}
          deOnde="Criada no funil, a partir do card"
          aoCriar={() => {
            invalidar([chaves.timeline()]);
            definirRecado('Tarefa criada.');
          }}
          aoFechar={() => definirCriandoTarefa(null)}
        />
      )}

      {escolhendoRotinas && escolhido && (
        <RotinasDoAtualizar
          funil={escolhido}
          nomeDoFunil={funilAberto?.nome ?? board?.funil.nome ?? 'Funil'}
          aoFechar={() => definirEscolhendoRotinas(false)}
          aoSalvar={(marcadas) => {
            definirEscolhendoRotinas(false);
            definirRecado(
              marcadas === 0
                ? 'O funil ficou sem o botão "Atualizar".'
                : `O "Atualizar" passa a rodar ${marcadas} ${marcadas === 1 ? 'rotina' : 'rotinas'}.`,
            );
          }}
        />
      )}
      {perdendo && (
        <DialogoDePerda
          titulo={perdendo.card.titulo ?? 'Esta oportunidade'}
          aoFechar={() => definirPerdendo(null)}
          aoConfirmar={(perda) => {
            mover(perdendo.card, perdendo.etapa, perda);
            definirPerdendo(null);
          }}
        />
      )}

      {criando && (
        <DialogoDeNovaOportunidade
          etapas={etapasEmOrdem}
          salvando={salvando}
          aoCriar={criarOportunidade}
          aoFechar={() => definirCriando(false)}
        />
      )}

      {vendo && (
        <DialogoDaOportunidade
          card={vendo}
          etapas={etapasEmOrdem}
          anotando={anotando}
          erroDaNota={erroDaNota}
          aoAnotar={anotar}
          aoEditar={() => definirEditando(vendo)}
          aoAbrirConversa={aoAbrirConversa}
          {...(aoAbrirCliente ? { aoAbrirCliente } : {})}
          aoFechar={() => {
            definirVendo(null);
            definirErroDaNota(null);
          }}
        />
      )}

      {pendente && (
        <PreencherParaMover
          pendente={pendente}
          salvando={salvandoPendente}
          erro={erroDoPendente}
          aoSalvar={preencherEMover}
          aoFechar={() => definirPendente(null)}
        />
      )}

      {editando && (
        <DialogoDeEdicaoDaOportunidade
          card={editando}
          salvando={salvandoEdicao}
          erro={erroDaEdicao}
          corrigindo={corrigindo}
          erroDaCorrecao={erroDaCorrecao}
          aoCorrigirNota={corrigirNota}
          aoSalvar={editarOportunidade}
          aoFechar={() => {
            definirEditando(null);
            definirErroDaEdicao(null);
            definirErroDaCorrecao(null);
          }}
        />
      )}

      {excluindo && (
        <Dialogo
          titulo="Excluir oportunidade?"
          /*
           * Enquanto exclui, não fecha: fechar pareceria cancelar, e o pedido
           * já saiu — o card sumiria depois, contra o que a pessoa escolheu.
           */
          aoFechar={() => {
            if (excluindoAgora) return;
            definirExcluindo(null);
            definirErroDaExclusao(null);
          }}
          botoes={
            <>
              <Botao
                variante="fantasma"
                desabilitado={excluindoAgora}
                motivo="Excluindo…"
                onClick={() => {
                  definirExcluindo(null);
                  definirErroDaExclusao(null);
                }}
              >
                Cancelar
              </Botao>
              <Botao
                variante="perigo"
                desabilitado={excluindoAgora}
                motivo="Excluindo…"
                onClick={excluirOportunidade}
              >
                {excluindoAgora ? 'Excluindo…' : 'Excluir'}
              </Botao>
            </>
          }
        >
          <p>
            <strong>{excluindo.titulo ?? 'Esta oportunidade'}</strong> sai do funil. Não dá para
            desfazer por aqui.
          </p>
          {erroDaExclusao && <Faixa tom="bloqueado">{erroDaExclusao}</Faixa>}
        </Dialogo>
      )}

      {recado && (
        <div className="recado" role="status" onAnimationEnd={() => definirRecado(null)}>
          {recado}
        </div>
      )}
    </div>
  );
}
